import { useSession } from './SessionProvider.js';
import { SignIn } from './SignIn.js';
import { Shell } from './Shell.js';
import { RepoProvider } from '../data/RepoProvider.js';
import { createSupabaseRepo } from '../data/supabaseRepo.js';
import { fixtureRepo } from '../data/fixtureRepo.js';
import { supabase } from '../lib/supabase.js';
import { ApprovalRoute, approvalToken } from '../approval/ApprovalRoute.js';

/**
 * Every state the session can be in gets a screen. `no_membership` and
 * `shop_unavailable` are not edge cases — the first is every invited user before
 * they accept, the second is any shop that stops paying.
 */
export function App() {
  const { outcome, loading, t } = useSession();

  // The approval link is answered BEFORE any session is considered. A customer
  // holding a link is not a user of this app and must never meet a sign-in
  // screen — checking the route first is what guarantees that.
  const token = approvalToken(window.location.pathname);
  if (token) return <ApprovalRoute token={token} />;

  if (loading) return <div className="boot">…</div>;

  switch (outcome.kind) {
    case 'signed_out':
      return <SignIn />;

    case 'no_membership':
      return (
        <Notice title="Your invitation is waiting">
          <p>
            <b>{outcome.user.email}</b> is signed in, but no shop has activated this
            account yet. Ask whoever invited you to finish setting it up.
          </p>
        </Notice>
      );

    case 'shop_unavailable':
      return (
        <Notice title={outcome.reason === 'closed' ? 'This shop is closed' : 'This shop is paused'}>
          <p>
            The account for your shop is not active, so its jobs are not available.
            Nothing has been deleted.
          </p>
        </Notice>
      );

    case 'load_failed':
      return (
        <Notice title="Could not load your shop">
          <p>
            You are still signed in — this is a failure to read, not a sign-out.
            Nothing has been lost.
          </p>
          <p className="mono small muted">{outcome.message}</p>
          <button className="btn primary" onClick={() => window.location.reload()}>
            Try again
          </button>
        </Notice>
      );

    case 'ready': {
      // The swap the whole data seam existed for. In preview there is no
      // database, so the fixtures stand in and the shell says so in the footer;
      // otherwise every screen is reading the real thing and not one of them
      // changed to make it so.
      const repo = import.meta.env.VITE_PREVIEW === '1'
        ? fixtureRepo
        : createSupabaseRepo(supabase, outcome.session.shop.id);
      return (
        <RepoProvider repo={repo}>
          <Shell />
        </RepoProvider>
      );
    }
  }
}

function Notice({ title, children }: { title: string; children: React.ReactNode }) {
  const { signOut } = useSession();
  return (
    <div className="notice-page">
      <div className="notice-card">
        <h1>{title}</h1>
        {children}
        <button className="btn" onClick={() => void signOut()}>Sign out</button>
      </div>
    </div>
  );
}
